import { PureQueryOptions } from '@apollo/client';
import Link, { ImperceptibleLink } from '@blackbird/ui-base/link';
import { MoreVert as MoreVertIcon, SettingsSharp } from '@mui/icons-material';
import { Card, CardContent, Divider, Grid, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import * as Schema from 'generated/graphql/schema';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import * as Highcharts from '@/components/containers/highcharts';
import { getNodeIcon } from '@/components/icons/node-icon';
import SignalAvatar from '@/components/icons/signal-avatar';
import DataChartConfig from '@/lib/highcharts/data-config';
import ManualProcessConfig from '@/lib/highcharts/manual-process-config';
import SensorSettingsDialog from '@/views/dialogs/sensor-settings/sensor-settings-dialog';

const useStyles = makeStyles()((theme: Theme) => ({
  card: {
    minHeight: '100%',
    transition: 'box-shadow .25s',
    '&:hover': {
      boxShadow:
        '0px 3px 5px -1px rgba(0, 0, 0, 0.2), 0px 5px 8px 0px rgba(0, 0, 0, 0.14), 0px 1px 14px 0px rgba(0, 0, 0, 0.12)',
    },
  },
  actions: {
    paddingLeft: theme.spacing(),
    paddingBottom: theme.spacing(),
    display: 'flex',
    justifyContent: 'space-around',
  },
  button: {
    margin: theme.spacing(),
  },
  nodeIcon: {
    height: 25,
    width: 25,
    marginRight: theme.spacing(),
    marginBottom: theme.spacing(-1),
  },
  menu: {},
  menuItemStyles: {
    padding: theme.spacing(),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  menuItemIcon: {
    minWidth: theme.spacing(4),
  },
}));

interface Properties {
  device: NonNullable<Schema.DevicesSimpleQuery['devices'][0]>;
  sensor: Pick<
    Schema.Sensor,
    'index' | 'name' | 'description' | 'config' | 'peripheralInformation' | 'peripheralId' | 'time'
  >;
  loading: boolean;
  refetchQueries?: PureQueryOptions[];
  dataHash: string;
}

const DeviceOverviewCard: React.FC<Properties> = ({ device, sensor, loading, refetchQueries, dataHash }) => {
  const [t, i18n] = useTranslation(['shared', 'device']);
  const { classes } = useStyles();
  const theme = useTheme();

  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const openMenu = Boolean(menuAnchorEl);
  const { uuid } = device;
  const { index, name, description, config } = sensor;
  const nodeType = sensor?.peripheralInformation?.node?.type ?? undefined;
  const online = (sensor?.time?.[0]?.stats?.data?.lineStatus?.status ?? Schema.Status.OFF) !== Schema.Status.OFF;

  const buttons = [
    {
      label: t(['shared:registerStops'], {
        defaultValue: 'Register Stops',
      }),
      uri: 'register-stops',
    },
    {
      label: t(['shared:analytics'], { defaultValue: 'Analytics' }),
      uri: 'analytics',
    },
  ];

  const isEnergyMeter = sensor.config.energyMeter === true;

  const openSettings = (e: React.MouseEvent) => {
    setMenuAnchorEl(null);
    setSettingsDialogOpen(true);
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <Grid item xs={12} sm={12} md={6}>
      <Card className={classes.card} elevation={1}>
        <ImperceptibleLink
          href={{
            pathname: '/sensor',
            query: { peripheralId: `${uuid}-${index}`, tab: 'live' },
          }}
        >
          <div className="rtl-direction" style={{ display: 'flex', padding: '16px 24px 16px 24px' }}>
            <div style={{ flex: '1 1 auto' }}>
              <Typography variant="body1">
                {nodeType ? getNodeIcon(classes.nodeIcon, nodeType) : null}
                {name}
              </Typography>
              <Typography color="textSecondary" variant="body2">
                {description}
              </Typography>
            </div>
            <div
              style={{
                flex: '0 0 auto',
                marginLeft: '15px',
                width: '40px',
                height: '40px',
              }}
            >
              <SignalAvatar online={online} uuid={uuid} />
            </div>
            <div
              style={{
                flex: '0 0 auto',
                marginLeft: '5px',
                width: '40px',
                height: '40px',
              }}
            >
              {isEnergyMeter ? (
                <IconButton className={classes.menu} aria-label="Settings" onClick={openSettings} size="large">
                  <SettingsSharp />
                </IconButton>
              ) : (
                <IconButton
                  className={classes.menu}
                  aria-label="More"
                  aria-owns={openMenu ? 'long-menu' : undefined}
                  aria-haspopup="true"
                  onClick={(e) => {
                    setMenuAnchorEl(e.currentTarget);
                    e.stopPropagation();
                    e.preventDefault();
                  }}
                  size="large"
                >
                  <MoreVertIcon />
                </IconButton>
              )}
              <Menu
                id="long-menu"
                anchorEl={menuAnchorEl}
                open={openMenu}
                onClose={(e) => {
                  setMenuAnchorEl(null);
                  // These functions exist even if TS doesn't think so
                  (e as any).stopPropagation();
                  (e as any).preventDefault();
                }}
                PaperProps={{
                  style: {},
                }}
              >
                {config && config.type === 'MEASUREMENT'
                  ? null
                  : buttons.map((btn) => (
                      <Link
                        key={btn.uri}
                        href={{
                          pathname: '/sensor',
                          query: {
                            peripheralId: `${uuid}-${index}`,
                            tab: btn.uri,
                          },
                        }}
                      >
                        <MenuItem key={btn.uri} selected={false}>
                          {btn.label}
                        </MenuItem>
                      </Link>
                    ))}
                <Divider variant="middle" />
                <MenuItem
                  key={'sensorSettings'}
                  selected={false}
                  onClick={openSettings}
                  className={classes.menuItemStyles}
                >
                  <ListItemIcon className={classes.menuItemIcon}>
                    <SettingsSharp />
                  </ListItemIcon>
                  <Typography variant="inherit" noWrap>
                    {t(['sensor:sensorSettings'], {
                      defaultValue: 'Sensor settings',
                    })}
                  </Typography>
                </MenuItem>
              </Menu>
            </div>
          </div>
          <CardContent className={'hideInPercy'} style={{ paddingBottom: 0 }}>
            {sensor?.config?.type !== Schema.SensorType.MANUAL_PROCESS ? (
              <Highcharts.Graph
                compareForUpdate={dataHash}
                configOptions={{
                  theme,
                  t,
                  language: i18n.language,
                  height: '180px',
                  sensors: [sensor],
                  disableTooltip: true,
                  disableExportMenu: true,
                  showLegend: false,
                }}
                configFn={DataChartConfig}
                loading={loading}
              />
            ) : (
              <Highcharts.Graph
                compareForUpdate={dataHash}
                configOptions={{
                  theme,
                  chartHeight: '180px',
                  data: sensor,
                  showLegend: false,
                }}
                configFn={ManualProcessConfig}
                loading={loading}
              />
            )}
          </CardContent>
        </ImperceptibleLink>
      </Card>
      <SensorSettingsDialog
        handleDialogChange={() => {
          setMenuAnchorEl(null);
          setSettingsDialogOpen(false);
        }}
        dialogOpen={settingsDialogOpen}
        peripheralId={sensor.peripheralId}
        refetchQueries={refetchQueries}
      />
    </Grid>
  );
};

export default DeviceOverviewCard;
